import {
    Sidebar,
    SidebarDivider,
    SidebarGroup,
    SidebarItem,
    SidebarScrollWrapper,
    SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

/**
 * Native NavContentBlueprint sidebar replacing the legacy Root component.
 *
 * All plugin nav items (API Docs, TechDocs, Home, Settings) are now provided
 * natively by their respective alpha FrontendPlugins, which are auto-discovered
 * via `app.packages: all`.  nav.take() renders each item in the correct position
 * and removes it from nav.rest() to avoid duplicate entries.
 *
 * nav.take() is also used to silently suppress items that should NOT appear in
 * the sidebar (catalog, catalog-graph, notifications, etc.).
 *
 * The native page:catalog-import item is suppressed so we can render our own
 * custom SidebarItem with a "Create..." label and icon at the same /catalog-import path.
 */
export const SidebarContent = NavContentBlueprint.make({
    params: {
        component: ({ navItems }) => {
            const nav = navItems.withComponent(item => (
                <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
            ));

            // Consume without rendering — keeps these out of nav.rest()
            nav.take('page:search');          // replaced by SidebarSearchModal
            nav.take('page:catalog');         // not in sidebar — accessed via search / Home
            nav.take('page:catalog-graph');   // accessible from entity pages, not in sidebar
            nav.take('page:catalog-import');  // native /catalog-import; /create used instead
            nav.take('page:app-visualizer');  // dev-only tool
            nav.take('page:notifications');   // replaced by NotificationsSidebarItem below
            nav.take('page:kubernetes');      // rendered on entity pages only, not in sidebar
            nav.take('page:scaffolder');      // /create already handles component registration

            return (
                <Sidebar>
                    <SidebarLogo />
                    <SidebarGroup label='Search' icon={<SearchIcon />} to='/search'>
                        <SidebarSearchModal />
                    </SidebarGroup>
                    <SidebarDivider />
                    <SidebarGroup label='Menu' icon={<MenuIcon />}>
                        {/* Home — native PageBlueprint from customPagesModule */}
                        {nav.take('page:app/home')}
                        {/* API Docs — native apiDocsPlugin (alpha, auto-discovered) */}
                        {nav.take('page:api-docs')}
                        {/* TechDocs — native techdocsPlugin (alpha, auto-discovered) */}
                        {nav.take('page:techdocs')}
                        {/* Register / Create — native catalogImportPlugin at /catalog-import */}
                        <SidebarItem icon={CreateComponentIcon} to='/catalog-import' text='Create...' />
                        <SidebarDivider />
                        {/* Bespoke pages (ADR, FAAST, Tech Radar, etc.) sorted alphabetically */}
                        <SidebarScrollWrapper>{nav.rest({ sortBy: 'title' })}</SidebarScrollWrapper>
                    </SidebarGroup>
                    <SidebarSpace />
                    <NotificationsSidebarItem />
                    <SidebarDivider />
                    <SidebarGroup label='Settings' icon={<UserSettingsSignInAvatar />} to='/settings'>
                        {nav.take('page:user-settings')}
                    </SidebarGroup>
                </Sidebar>
            );
        },
    },
});
